import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, downloadFile, downloadFilePost } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, refreshCounts } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import {
  Upload, RefreshCw, Trash2, X, Download, Search, Sparkles, Wand2,
  ChevronDown, ChevronUp, Rows2, Rows3, Loader2, Copy, ShieldCheck,
  AlertTriangle, Image as ImageIcon, Camera, FileText,
} from "lucide-react";
import { useAuth, usePerms } from "@/lib/auth";
import ImageThumb from "@/components/ImageThumb";

const MARKETPLACES = ["amazon", "flipkart", "myntra", "shopify"];
const MAX_FILES = 4;

const ALL_COLUMNS = [
  { key: "image",   label: "",         width: 50,  compact: true, align: "center" },
  { key: "sku",     label: "SKU",      width: 140, compact: true, mono: true },
  { key: "name",    label: "Product",  width: 320, compact: true },
  { key: "brand",   label: "Brand",    width: 130, compact: false },
  { key: "type",    label: "Type",     width: 110, compact: false },
  { key: "sp",      label: "SP ₹",     width: 90,  compact: true,  mono: true, align: "right" },
  { key: "mrp",     label: "MRP ₹",    width: 90,  compact: false, mono: true, align: "right" },
  { key: "uploads", label: "Uploads",  width: 70,  compact: false, mono: true, align: "right" },
  { key: "gen",     label: "Gen Img",  width: 80,  compact: true,  align: "center" },
  { key: "status",  label: "Status",   width: 100, compact: true,  align: "center" },
  { key: "updated", label: "Updated",  width: 100, compact: false, mono: true },
];

function getCell(r, k) {
  const info = r.product_info || {};
  const al = r.amazon_listing || {};
  switch (k) {
    case "sku":     return r.sku || "";
    case "name":    return al.title || info.product_name || "";
    case "brand":   return info.brand || "";
    case "type":    return info.product_type || "";
    case "sp":      return r.selling_price ?? "";
    case "mrp":     return r.mrp ?? "";
    case "uploads": return (r.uploads || []).length;
    case "gen":     return Object.keys(r.generated_images || {}).length;
    case "status":  return r.job_status || "";
    case "updated": return r.updated_at || "";
    default: return "";
  }
}

function StatusPill({ status }) {
  const map = {
    done:    { bg: "bg-emerald-50", tx: "text-emerald-800", bd: "border-emerald-200", label: "Ready" },
    running: { bg: "bg-blue-50",    tx: "text-blue-800",    bd: "border-blue-200",    label: "Running" },
    failed:  { bg: "bg-red-50",     tx: "text-red-800",     bd: "border-red-200",     label: "Failed" },
  };
  const t = map[status] || map.running;
  return (
    <span data-testid={`master-status-${status}`}
          className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${t.bg} ${t.tx} ${t.bd}`}>
      {status === "running" && <Loader2 size={9} className="animate-spin" />}
      {t.label}
    </span>
  );
}

function ago(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function MasterData() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [wide, setWide] = useState(false);
  const [sort, setSort] = useState({ key: "updated", dir: "desc" });
  const [selected, setSelected] = useState(() => new Set());
  const [detail, setDetail] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const { user } = useAuth();
  const { canEdit } = usePerms();
  const editable = canEdit("master") || user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/master", { params: q ? { q } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Poll running rows so status transitions to Ready live in the grid
  useEffect(() => {
    const running = rows.filter((r) => r.job_status === "running");
    if (!running.length) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [rows]);

  // Poll open drawer while it's still running
  useEffect(() => {
    if (!detail?.id || detail.job_status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/master/${detail.id}`);
        setDetail(data);
      } catch (_e) { /* silent */ }
    }, 5000);
    return () => clearInterval(t);
  }, [detail?.id, detail?.job_status]);

  const filteredRows = useMemo(() => {
    const gs = q.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (!gs) return true;
      const hay = [r.sku, r.product_info?.product_name, r.product_info?.brand, r.amazon_listing?.title].join(" ").toLowerCase();
      return hay.includes(gs);
    });
    out.sort((a, b) => {
      const av = getCell(a, sort.key), bv = getCell(b, sort.key);
      const an = typeof av === "number" ? av : parseFloat(av);
      const bn = typeof bv === "number" ? bv : parseFloat(bv);
      const cmp = (!isNaN(an) && !isNaN(bn) && av !== "" && bv !== "")
        ? an - bn : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, sort]);

  const visibleColumns = useMemo(
    () => (wide ? ALL_COLUMNS : ALL_COLUMNS.filter((c) => c.compact)), [wide]
  );

  const openDetail = async (r) => {
    try { const { data } = await api.get(`/master/${r.id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record and its images?")) return;
    try { await api.delete(`/master/${id}`); toast.success("Deleted"); setDetail(null); load(); refreshCounts(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filteredRows.forEach((r) => next.delete(r.id));
    else filteredRows.forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulkExport = async (m) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await downloadFilePost(`/master/export/${m}.csv`, { ids }, `master-${m}-${ids.length}.csv`);
      toast.success(`Exported ${ids.length} to ${m}`);
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  const setSortKey = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  return (
    <>
      <PageHeader title="Master Data" subtitle="Upload product photos → AI extracts info → Amazon listing + 7 marketing images (image gen is FREE)"
                  testId="module-title-master" module="master">
        <div className="flex items-center gap-1.5">
          <button data-testid="master-toggle-wide" onClick={() => setWide((w) => !w)}
                  className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${wide ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
            {wide ? <Rows3 size={12} /> : <Rows2 size={12} />} {wide ? "Wide" : "Compact"}
          </button>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="master-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()}
                   placeholder="Search SKU / product…"
                   className="h-8 pl-7 pr-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          {editable && (
            <Button variant="primary" onClick={() => setShowUpload(true)} testId="master-new-btn">
              <Upload size={13} /> Upload photos
            </Button>
          )}
        </div>
      </PageHeader>

      <PageBody>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-180px)]">
            <table className="erp-table w-full" data-testid="master-grid">
              <thead className="sticky top-0 z-10 bg-slate-100">
                <tr>
                  <th style={{ width: 32 }} className="text-center">
                    <input type="checkbox" data-testid="master-select-all" checked={allSelected} onChange={toggleAll} className="accent-indigo-600" />
                  </th>
                  {visibleColumns.map((c) => (
                    <th key={c.key} style={{ width: c.width, minWidth: c.width, textAlign: c.align || "left" }}
                        className="cursor-pointer select-none hover:bg-slate-200"
                        onClick={() => c.key !== "image" && setSortKey(c.key)}
                        data-testid={`master-col-header-${c.key}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {c.label}
                        {sort.key === c.key && c.key !== "image" && (sort.dir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const rowSelected = selected.has(r.id);
                  const genCount = Object.keys(r.generated_images || {}).length;
                  const firstImg = r.generated_images?.master?.storage_path
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/master/${r.id}/images/master`
                    : (r.uploads?.[0]?.storage_path
                      ? `${process.env.REACT_APP_BACKEND_URL}/api/master/${r.id}/uploads/0`
                      : null);
                  return (
                    <tr key={r.id} data-testid={`master-row-${r.id}`}
                        className={`border-t border-slate-100 hover:bg-indigo-50/40 ${rowSelected ? "bg-blue-50/60" : ""}`}
                        onClick={() => openDetail(r)}>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`master-row-select-${r.id}`} checked={rowSelected}
                               onChange={() => toggleOne(r.id)} className="accent-indigo-600" />
                      </td>
                      {visibleColumns.map((c) => {
                        const align = c.align || "left";
                        const mono = c.mono ? "font-mono" : "";
                        const common = `text-[12px] ${mono}`;
                        switch (c.key) {
                          case "image":
                            return (
                              <td key={c.key} className="p-1">
                                {firstImg
                                  ? <ImageThumb src={firstImg} size={64} fit="contain" testId={`master-thumb-${r.id}`} />
                                  : <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-sm flex items-center justify-center text-slate-300"><ImageIcon size={14} /></div>}
                              </td>
                            );
                          case "sku":
                            return <td key={c.key} className={common}>{r.sku}</td>;
                          case "name":
                            return (
                              <td key={c.key} className="text-[12px] font-medium text-slate-800 truncate max-w-[320px]" title={r.amazon_listing?.title || r.product_info?.product_name || ""}>
                                {r.amazon_listing?.title || r.product_info?.product_name || <span className="text-slate-400 italic">Analysing…</span>}
                              </td>
                            );
                          case "brand":
                            return <td key={c.key} className={common}>{r.product_info?.brand || "—"}</td>;
                          case "type":
                            return <td key={c.key} className={common}>{r.product_info?.product_type || "—"}</td>;
                          case "sp":
                            return <td key={c.key} className={`${common} text-right font-semibold text-emerald-800`}>{r.selling_price != null ? `₹${r.selling_price}` : "—"}</td>;
                          case "mrp":
                            return <td key={c.key} className={`${common} text-right text-slate-500`}>{r.mrp != null ? `₹${r.mrp}` : "—"}</td>;
                          case "uploads":
                            return <td key={c.key} className={`${common} text-right text-slate-600`}>{(r.uploads || []).length}</td>;
                          case "gen":
                            return <td key={c.key} className="text-center">
                              {genCount > 0
                                ? <span className="inline-flex items-center gap-0.5 text-indigo-700 font-mono text-[11px]"><Sparkles size={10} />{genCount}</span>
                                : (r.job_status === "running"
                                  ? <span className="inline-flex items-center gap-0.5 text-blue-600 text-[11px]"><Loader2 size={10} className="animate-spin" /></span>
                                  : <span className="text-slate-300">—</span>)}
                            </td>;
                          case "status":
                            return <td key={c.key} className="text-center"><StatusPill status={r.job_status} /></td>;
                          case "updated":
                            return <td key={c.key} className="text-[11px] font-mono text-slate-500">{ago(r.updated_at)}</td>;
                          default: return <td key={c.key} />;
                        }
                      })}
                    </tr>
                  );
                })}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="p-10 text-center text-slate-500 text-[13px] border-t border-slate-100">
                      {rows.length === 0
                        ? <>
                            <div className="mb-2">No master data yet.</div>
                            {editable && <button onClick={() => setShowUpload(true)} className="inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid="master-empty-upload">
                              <Upload size={12} /> Upload your first product photos
                            </button>}
                          </>
                        : "No rows match your search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer / bulk */}
        <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-600">
          <div className="font-mono" data-testid="master-row-count">
            {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"}
            {selected.size > 0 && <span className="ml-2 text-indigo-700 font-semibold">• {selected.size} selected</span>}
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="master-bulk-action-bar">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Bulk export:</span>
              {MARKETPLACES.map((m) => (
                <button key={m} onClick={() => bulkExport(m)} data-testid={`master-bulk-export-${m}`}
                        className="h-7 px-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-sm text-[11.5px] font-semibold capitalize">
                  <Download size={11} /> {m}
                </button>
              ))}
              <button onClick={() => setSelected(new Set())} className="h-7 px-2 inline-flex items-center gap-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 rounded-sm text-[11.5px]">
                <X size={11} /> Clear
              </button>
            </div>
          )}
        </div>
      </PageBody>

      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)}
                     onCreated={(r) => { setShowUpload(false); setDetail(r); load(); refreshCounts(); }} />
      )}
      {detail && (
        <DetailDrawer rid={detail.id} detail={detail} onClose={() => setDetail(null)}
                      onChange={(d) => setDetail(d)} onRefresh={load} onDelete={() => remove(detail.id)}
                      editable={editable} />
      )}
    </>
  );
}

// ---------------- Upload modal --------------------------------------------

function UploadModal({ onClose, onCreated }) {
  const [files, setFiles] = useState([]);
  const [sku, setSku] = useState("");
  const [sp, setSp] = useState("");
  const [mrp, setMrp] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list).slice(0, MAX_FILES - files.length);
    setFiles((f) => [...f, ...arr].slice(0, MAX_FILES));
  };
  const remove = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length) return toast.error("Add at least 1 photo");
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (sku) fd.append("sku", sku);
      if (sp) fd.append("selling_price", sp);
      if (mrp) fd.append("mrp", mrp);
      const { data } = await api.post("/master", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded. AI is analysing… this takes ~60-90s.");
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-[720px] max-w-full max-h-[90vh] bg-white border border-slate-200 rounded-sm flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="h-12 border-b border-slate-200 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Camera size={16} className="text-indigo-600" />
            <span className="font-heading text-[14px] font-semibold text-slate-900">New master data record</span>
          </div>
          <button onClick={onClose} data-testid="master-upload-close" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
        </div>
        <div className="p-4 flex-1 overflow-auto">
          <div className="text-[11.5px] text-slate-600 mb-3 leading-relaxed">
            Upload <strong>1–4 photos</strong> of the same product from different angles. Our AI (ChatGPT vision) will
            identify the product, generate an Amazon India–compliant listing, and produce a pure-white master image plus 6 marketing feature images. <strong className="text-emerald-700">Image generation is 100% free</strong> (rembg + Pollinations, no credits).
          </div>

          {/* Drop / picker */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-sm p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40"
            onClick={() => inputRef.current?.click()}
            data-testid="master-upload-dropzone">
            <Upload size={22} className="mx-auto text-slate-400 mb-2" />
            <div className="text-[13px] font-semibold text-slate-800">Drop images here or click to browse</div>
            <div className="text-[11px] text-slate-500 mt-0.5">JPG / PNG / WebP · up to {MAX_FILES} files · 8 MB each</div>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
                   data-testid="master-upload-input"
                   onChange={(e) => addFiles(e.target.files)} />
          </div>

          {files.length > 0 && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {files.map((f, i) => (
                <div key={i} className="relative border border-slate-200 rounded-sm overflow-hidden aspect-square bg-slate-100" data-testid={`master-preview-${i}`}>
                  <img referrerPolicy="no-referrer" src={URL.createObjectURL(f)} alt="" className="w-full h-full object-contain" />
                  <button onClick={() => remove(i)} className="absolute top-1 right-1 w-5 h-5 bg-slate-900/70 text-white rounded-full flex items-center justify-center hover:bg-slate-900"
                          data-testid={`master-preview-remove-${i}`}>
                    <X size={10} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-900/60 text-white text-[10px] truncate px-1 py-0.5 font-mono">{f.name}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">SKU (optional)</div>
              <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="auto-generated"
                     data-testid="master-upload-sku"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Selling price ₹</div>
              <input type="number" value={sp} onChange={(e) => setSp(e.target.value)}
                     data-testid="master-upload-sp"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">MRP ₹</div>
              <input type="number" value={mrp} onChange={(e) => setMrp(e.target.value)}
                     data-testid="master-upload-mrp"
                     className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
            </div>
          </div>
        </div>
        <div className="h-12 border-t border-slate-200 flex items-center justify-end gap-2 px-4">
          <button onClick={onClose} className="text-[12px] text-slate-600 hover:text-slate-900 px-3 h-8" data-testid="master-upload-cancel">Cancel</button>
          <Button variant="primary" onClick={submit} disabled={uploading || !files.length} testId="master-upload-submit">
            {uploading ? <><RefreshCw size={12} className="animate-spin" /> Uploading…</> : <><Sparkles size={12} /> Analyse & generate</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Detail drawer -------------------------------------------

function DetailDrawer({ rid, detail, onClose, onChange, onRefresh, onDelete, editable }) {
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  const info = detail.product_info || {};
  const al = detail.amazon_listing;
  const gi = detail.generated_images || {};
  const isRunning = detail.job_status === "running";
  const backend = process.env.REACT_APP_BACKEND_URL;
  const imgUrl = (slot) => `${backend}/api/master/${rid}/images/${slot}`;
  const uploadUrl = (i) => `${backend}/api/master/${rid}/uploads/${i}`;

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/master/${rid}`, payload);
      onChange(data); toast.success("Saved"); onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const regenAll = async () => {
    setRegenBusy(true);
    try { await api.post(`/master/${rid}/regenerate`); toast.info("Full pipeline restarted"); onRefresh(); }
    catch (e) { toast.error(formatApiError(e)); }
    setRegenBusy(false);
  };
  const regenListing = async () => {
    setRegenBusy(true);
    try { const { data } = await api.post(`/master/${rid}/regenerate-listing`); onChange(data); toast.success("Listing regenerated"); onRefresh(); }
    catch (e) { toast.error(formatApiError(e)); }
    setRegenBusy(false);
  };

  const downloadOne = (m) => downloadFile(`/master/${rid}/export/${m}.csv`, `master-${detail.sku}-${m}.csv`);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div className="w-[960px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <StatusPill status={detail.job_status} />
            <div className="min-w-0">
              <div className="font-heading text-[14px] font-semibold text-slate-900 truncate">
                {al?.title || info.product_name || <span className="text-slate-400 italic">Analysing…</span>}
              </div>
              <div className="font-mono text-[11px] text-slate-500">{detail.sku}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="master-detail-close" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 flex flex-wrap items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Export:</span>
          {MARKETPLACES.map((m) => (
            <button key={m} onClick={() => downloadOne(m)} data-testid={`master-download-${m}`}
                    className="h-7 px-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-sm text-[11.5px] font-semibold capitalize">
              <Download size={11} /> {m}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            {editable && (
              <button onClick={regenListing} disabled={regenBusy || isRunning} data-testid="master-regen-listing"
                      className="text-[11.5px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                <FileText size={11} /> Regen listing
              </button>
            )}
            {editable && (
              <button onClick={regenAll} disabled={regenBusy || isRunning} data-testid="master-regen-all"
                      className="text-[11.5px] text-indigo-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                {isRunning ? <><Loader2 size={11} className="animate-spin" /> Running…</> : <><Wand2 size={11} /> Regen all</>}
              </button>
            )}
            {editable && (
              <button onClick={onDelete} data-testid="master-delete"
                      className="text-[11.5px] text-red-600 hover:underline inline-flex items-center gap-1">
                <Trash2 size={11} /> Delete
              </button>
            )}
          </div>
        </div>

        <div className="border-b border-slate-200 bg-white px-4 flex items-center gap-4 shrink-0">
          {["overview", "images", "amazon", "raw"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`master-tab-${t}`}
                    className={`h-9 text-[12px] font-semibold border-b-2 capitalize ${tab === t ? "border-indigo-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              {t === "amazon" ? "Amazon Listing" : t === "raw" ? "Vision JSON" : t}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {detail.job_error && (
            <div className="m-4 p-3 border border-red-300 bg-red-50 rounded-sm text-[12px] text-red-800">
              <strong>Job error:</strong> {detail.job_error}
            </div>
          )}
          {isRunning && (
            <div className="m-4 p-3 border border-blue-300 bg-blue-50 rounded-sm text-[12px] text-blue-800 inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              <span>AI is running (stage: <strong>{detail.job_stage}</strong>). Total time ~60-90s.</span>
            </div>
          )}

          {tab === "overview" && <OverviewTab detail={detail} uploadUrl={uploadUrl} saving={saving} onSave={patch} editable={editable} />}
          {tab === "images" && <ImagesTab gi={gi} imgUrl={imgUrl} uploads={detail.uploads || []} uploadUrl={uploadUrl} />}
          {tab === "amazon" && <AmazonTab listing={al} generatedAt={detail.updated_at} isRunning={isRunning} onRegen={regenListing} regenBusy={regenBusy} editable={editable} />}
          {tab === "raw" && (
            <div className="p-4">
              <pre className="text-[11px] font-mono whitespace-pre-wrap text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-3 max-h-[70vh] overflow-auto">
                {JSON.stringify(info, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ detail, uploadUrl, saving, onSave, editable }) {
  const info = detail.product_info || {};
  const [sp, setSp] = useState(detail.selling_price ?? "");
  const [mrp, setMrp] = useState(detail.mrp ?? "");
  const [sku, setSku] = useState(detail.sku || "");
  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Uploaded photos ({(detail.uploads || []).length})</div>
        <div className="flex gap-2 flex-wrap">
          {(detail.uploads || []).map((_up, i) => (
            <a key={i} href={uploadUrl(i)} target="_blank" rel="noreferrer" data-testid={`master-upload-thumb-${i}`}
               className="w-28 h-28 shrink-0 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden flex items-center justify-center hover:border-indigo-300">
              <img referrerPolicy="no-referrer" src={uploadUrl(i)} alt="" className="max-w-full max-h-full object-contain" />
            </a>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">AI-extracted product info</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 border border-slate-200 rounded-sm bg-slate-50 p-3">
          {Object.entries(info).filter(([k]) => k !== "raw" && k !== "error" && k !== "features").map(([k, v]) => (
            <div key={k} className="flex text-[12px]" data-testid={`master-info-${k}`}>
              <span className="w-32 font-mono text-slate-500">{k}</span>
              <span className="flex-1 text-slate-800">{typeof v === "string" ? (v || "—") : JSON.stringify(v)}</span>
            </div>
          ))}
        </div>
        {(info.features || []).length > 0 && (
          <div className="mt-2">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">features</div>
            <ul className="list-disc pl-5 text-[12px] text-slate-800 space-y-0.5">
              {(info.features || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Pricing</div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10.5px] text-slate-500 font-semibold mb-1">SKU</div>
            <input value={sku} onChange={(e) => setSku(e.target.value)} disabled={!editable}
                   data-testid="master-field-sku"
                   className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
          </div>
          <div>
            <div className="text-[10.5px] text-slate-500 font-semibold mb-1">Selling price ₹</div>
            <input type="number" value={sp} onChange={(e) => setSp(e.target.value)} disabled={!editable}
                   data-testid="master-field-sp"
                   className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
          </div>
          <div>
            <div className="text-[10.5px] text-slate-500 font-semibold mb-1">MRP ₹</div>
            <input type="number" value={mrp} onChange={(e) => setMrp(e.target.value)} disabled={!editable}
                   data-testid="master-field-mrp"
                   className="w-full h-8 px-2 text-[13px] border border-slate-200 rounded-sm font-mono focus:ring-1 focus:ring-indigo-500 outline-none" />
          </div>
        </div>
        {editable && (
          <div className="mt-3 flex justify-end">
            <Button variant="primary" testId="master-save-btn" disabled={saving}
                    onClick={() => onSave({ sku, selling_price: sp === "" ? null : parseFloat(sp), mrp: mrp === "" ? null : parseFloat(mrp) })}>
              {saving ? <><RefreshCw size={12} className="animate-spin" /> Saving…</> : "Save changes"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ImagesTab({ gi, imgUrl, uploads, uploadUrl }) {
  const slots = ["master", "lifestyle", "features", "dimensions", "material", "angles", "package"];
  const gen = slots.filter((s) => gi[s]?.storage_path);
  return (
    <div className="p-4 space-y-4">
      {gen.length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Generated images ({gen.length}) — free (rembg + Pollinations)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {gen.map((s) => (
              <div key={s} data-testid={`master-img-${s}`} className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
                <div className="aspect-square bg-white flex items-center justify-center">
                  <img referrerPolicy="no-referrer" src={imgUrl(s)} alt={gi[s].label} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="border-t border-slate-200 px-2 py-1 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-800">{gi[s].label}</span>
                  <a href={imgUrl(s)} download target="_blank" rel="noreferrer"
                     className="text-[10.5px] text-blue-600 hover:underline inline-flex items-center gap-0.5">
                    <Download size={9} /> {gi[s].method}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {uploads.length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Uploaded originals ({uploads.length})</div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {uploads.map((_up, i) => (
              <a key={i} href={uploadUrl(i)} target="_blank" rel="noreferrer"
                 className="w-28 h-28 shrink-0 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden flex items-center justify-center hover:border-indigo-300">
                <img referrerPolicy="no-referrer" src={uploadUrl(i)} alt="" className="max-w-full max-h-full object-contain" />
              </a>
            ))}
          </div>
        </div>
      )}
      {gen.length === 0 && uploads.length === 0 && (
        <div className="p-8 text-center text-slate-500 text-[13px]">No images.</div>
      )}
    </div>
  );
}

function AmazonTab({ listing, generatedAt, isRunning, onRegen, regenBusy, editable }) {
  if (!listing) {
    return (
      <div className="p-6 text-center">
        <ShieldCheck size={24} className="mx-auto text-indigo-500 mb-2" />
        <div className="text-[13px] font-semibold text-slate-800">Amazon listing not generated yet.</div>
        {isRunning && <div className="text-[11.5px] text-slate-500 mt-2">Waiting for the pipeline to finish…</div>}
      </div>
    );
  }
  const warnings = listing.warnings || [];
  const titleLen = (listing.title || "").length;
  const searchBytes = new TextEncoder().encode(listing.search_terms || "").length;
  const descWords = (listing.description || "").split(/\s+/).filter(Boolean).length;
  const copy = (t, l) => { navigator.clipboard.writeText(t || ""); toast.success(`${l} copied`); };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {warnings.length === 0
            ? <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10.5px] font-bold uppercase tracking-wider"><ShieldCheck size={11} /> Policy compliant</span>
            : <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-amber-50 text-amber-900 border border-amber-300 text-[10.5px] font-bold uppercase tracking-wider" data-testid="master-amazon-warnings"><AlertTriangle size={11} /> {warnings.length} warning{warnings.length === 1 ? "" : "s"}</span>}
          {generatedAt && <span className="text-[10.5px] text-slate-500 font-mono">updated {ago(generatedAt)}</span>}
        </div>
        {editable && (
          <Button onClick={onRegen} disabled={regenBusy || isRunning} testId="master-amazon-regen">
            {regenBusy ? <><RefreshCw size={12} className="animate-spin" /> Generating…</> : <><Wand2 size={12} /> Regenerate</>}
          </Button>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm text-[11.5px] text-amber-900">
          <div className="font-semibold mb-1">Policy warnings:</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <Block title={`Title • ${titleLen}/75 chars`} onCopy={() => copy(listing.title, "Title")} testId="master-amazon-title">
        <div className={`p-2 text-[13px] font-medium ${titleLen > 75 ? "text-red-700 bg-red-50" : "text-slate-900 bg-slate-50"} border border-slate-200 rounded-sm`}>
          {listing.title || <span className="text-slate-400">—</span>}
        </div>
      </Block>
      <Block title={`Bullets • ${(listing.bullets || []).length}/5`} onCopy={() => copy((listing.bullets || []).join("\n"), "Bullets")} testId="master-amazon-bullets">
        <ul className="space-y-1 border border-slate-200 rounded-sm bg-slate-50 p-2">
          {(listing.bullets || []).map((b, i) => (
            <li key={i} className="flex gap-2 text-[12.5px]" data-testid={`master-amazon-bullet-${i}`}>
              <span className={`shrink-0 font-mono text-[10.5px] w-14 text-right pt-0.5 ${b.length < 10 || b.length > 200 ? "text-red-600" : "text-slate-400"}`}>[{b.length}]</span>
              <span className="text-slate-800">{b}</span>
            </li>
          ))}
        </ul>
      </Block>
      <Block title={`Description • ${descWords} words`} onCopy={() => copy(listing.description, "Description")} testId="master-amazon-description">
        <div className="p-2 text-[12.5px] text-slate-800 whitespace-pre-wrap border border-slate-200 rounded-sm bg-slate-50 max-h-56 overflow-auto">
          {listing.description || <span className="text-slate-400">—</span>}
        </div>
      </Block>
      <Block title={`Backend Search Terms • ${searchBytes}/250 bytes`} onCopy={() => copy(listing.search_terms, "Search terms")} testId="master-amazon-search-terms">
        <div className={`p-2 text-[12px] font-mono border border-slate-200 rounded-sm ${searchBytes > 250 ? "text-red-700 bg-red-50" : "text-slate-800 bg-slate-50"}`}>
          {listing.search_terms || <span className="text-slate-400">—</span>}
        </div>
      </Block>
      <Block title="Specifications" onCopy={() => copy(Object.entries(listing.specifications || {}).map(([k, v]) => `${k}: ${v}`).join("\n"), "Specs")} testId="master-amazon-specs">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 border border-slate-200 rounded-sm bg-slate-50 p-3">
          {Object.entries(listing.specifications || {}).map(([k, v]) => (
            <div key={k} className="flex text-[12px]">
              <span className="w-40 font-mono text-slate-500">{k}</span>
              <span className="flex-1 text-slate-800 font-medium">{v}</span>
            </div>
          ))}
        </div>
      </Block>
      <Block title="Search keywords" onCopy={() => copy(JSON.stringify(listing.keywords, null, 2), "Keywords")} testId="master-amazon-keywords">
        {["primary", "secondary", "long_tail"].map((k) => (
          <div key={k} className="mb-1">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{k.replace("_", "-")}</div>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {(listing.keywords?.[k] || []).map((w, i) => (
                <span key={i} className="inline-flex items-center h-5 px-1.5 rounded-sm bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px]">{w}</span>
              ))}
              {(listing.keywords?.[k] || []).length === 0 && <span className="text-[11px] text-slate-400 italic">—</span>}
            </div>
          </div>
        ))}
      </Block>
    </div>
  );
}

function Block({ title, children, onCopy, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
        <button onClick={onCopy} className="inline-flex items-center gap-1 text-[10.5px] text-blue-600 hover:underline">
          <Copy size={10} /> copy
        </button>
      </div>
      {children}
    </div>
  );
}
